import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getOnboardingJourneys, saveOnboardingJourney, GetOnboardingJourneysOutputType } from 'zite-endpoints-sdk';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ClipboardList, Plus, UserPlus, CheckSquare } from 'lucide-react';
import { isManagerPlus } from '../lib/roles';

export default function OnboardingJourneysPage() {
  const { user } = useAuth();
  const [data, setData] = useState<GetOnboardingJourneysOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [journeyDialog, setJourneyDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [editJourney, setEditJourney] = useState<any>(null);
  const [assignForm, setAssignForm] = useState({ employeeId: '', journeyId: '' });

  useEffect(() => { if (user) load(); }, [user]);
  const load = () => { setLoading(true); getOnboardingJourneys({}).then(setData).finally(() => setLoading(false)); };

  const manager = isManagerPlus(user?.role);
  const myAssignments = (data?.assignments || []).filter((a: any) => { const eid = Array.isArray(a.employee) ? a.employee[0] : a.employee; return eid === user?.id; });

  const handleSaveJourney = async () => {
    if (!editJourney?.journeyName) return;
    try {
      await saveOnboardingJourney({ action: 'saveJourney', ...editJourney });
      toast.success('Journey saved');
      setJourneyDialog(false);
      load();
    } catch { toast.error('Failed'); }
  };

  const handleAssign = async () => {
    if (!assignForm.employeeId || !assignForm.journeyId) return;
    try {
      const emp = data?.staffList.find((s: any) => s.id === assignForm.employeeId);
      await saveOnboardingJourney({ action: 'assignJourney', employeeId: assignForm.employeeId, journeyId: assignForm.journeyId, assignmentName: `${emp?.firstName || ''} Onboarding` });
      toast.success('Journey assigned');
      setAssignDialog(false);
      load();
    } catch { toast.error('Failed'); }
  };

  const toggleStep = async (assignment: any, stepIndex: number, isManager: boolean) => {
    const field = isManager ? 'managerCompletedSteps' : 'completedSteps';
    const current: number[] = JSON.parse(assignment[field] || '[]');
    const updated = current.includes(stepIndex) ? current.filter(i => i !== stepIndex) : [...current, stepIndex];
    const journey = data?.journeys.find((j: any) => { const jid = Array.isArray(assignment.journey) ? assignment.journey[0] : assignment.journey; return j.id === jid; });
    const totalItems = JSON.parse(journey?.checklistItems || '[]').length + JSON.parse(journey?.managerChecklistItems || '[]').length;
    const completedSteps = JSON.parse(assignment.completedSteps || '[]');
    const managerSteps = JSON.parse(assignment.managerCompletedSteps || '[]');
    const allCompleted = isManager ? [...completedSteps, ...updated] : [...updated, ...managerSteps];
    const prog = totalItems > 0 ? allCompleted.length / totalItems : 0;

    try {
      await saveOnboardingJourney({
        action: 'updateAssignment', id: assignment.id,
        [field]: JSON.stringify(updated),
        progress: prog,
        status: prog >= 1 ? 'Completed' : 'In Progress',
      });
      load();
    } catch { toast.error('Failed to update'); }
  };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ClipboardList className="w-6 h-6 text-primary" /> Onboarding Journeys</h1>
          <p className="text-sm text-muted-foreground mt-1">{manager ? 'Manage onboarding templates & staff assignments' : 'Your onboarding checklist'}</p>
        </div>
        {manager && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => { setAssignForm({ employeeId: '', journeyId: '' }); setAssignDialog(true); }}>
              <UserPlus className="w-4 h-4 mr-1" /> Assign
            </Button>
            <Button size="sm" onClick={() => { setEditJourney({ journeyName: '', description: '', targetRole: '', targetDepartment: '', checklistItems: '[]', managerChecklistItems: '[]', status: 'Draft' }); setJourneyDialog(true); }}>
              <Plus className="w-4 h-4 mr-1" /> New Journey
            </Button>
          </div>
        )}
      </div>

      <Tabs defaultValue={myAssignments.length > 0 ? 'my' : 'templates'}>
        <TabsList>
          {myAssignments.length > 0 && <TabsTrigger value="my">My Onboarding</TabsTrigger>}
          {manager && <TabsTrigger value="templates">Templates ({data?.journeys.length || 0})</TabsTrigger>}
          {manager && <TabsTrigger value="assignments">All Assignments ({data?.assignments.length || 0})</TabsTrigger>}
        </TabsList>

        {myAssignments.length > 0 && (
          <TabsContent value="my" className="space-y-4 mt-4">
            {myAssignments.map((a: any) => {
              const journey = data?.journeys.find((j: any) => { const jid = Array.isArray(a.journey) ? a.journey[0] : a.journey; return j.id === jid; });
              const items: string[] = JSON.parse(journey?.checklistItems || '[]');
              const completed: number[] = JSON.parse(a.completedSteps || '[]');
              return (
                <Card key={a.id}>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-base">{journey?.journeyName || 'Onboarding'}</CardTitle>
                      <Badge variant={a.status === 'Completed' ? 'default' : 'outline'}>{a.status}</Badge>
                    </div>
                    <Progress value={(a.progress || 0) * 100} className="mt-2" />
                    <p className="text-xs text-muted-foreground">{Math.round((a.progress || 0) * 100)}% complete</p>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {items.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Checkbox checked={completed.includes(idx)} onCheckedChange={() => toggleStep(a, idx, false)} />
                        <span className={`text-sm ${completed.includes(idx) ? 'line-through text-muted-foreground' : ''}`}>{item}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>
        )}

        {manager && (
          <TabsContent value="templates" className="space-y-3 mt-4">
            {(data?.journeys || []).map((j: any) => (
              <Card key={j.id} className="cursor-pointer hover:shadow-md" onClick={() => { setEditJourney(j); setJourneyDialog(true); }}>
                <CardContent className="py-4 flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold">{j.journeyName}</h3>
                    <p className="text-sm text-muted-foreground">{j.description || 'No description'}</p>
                    <div className="flex gap-2 mt-1">{j.targetRole && <Badge variant="outline" className="text-xs">{j.targetRole}</Badge>}{j.targetDepartment && <Badge variant="outline" className="text-xs">{j.targetDepartment}</Badge>}</div>
                  </div>
                  <Badge variant={j.status === 'Active' ? 'default' : 'outline'}>{j.status}</Badge>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        )}

        {manager && (
          <TabsContent value="assignments" className="space-y-3 mt-4">
            {(data?.assignments || []).map((a: any) => {
              const emp = data?.staffList.find((s: any) => { const eid = Array.isArray(a.employee) ? a.employee[0] : a.employee; return s.id === eid; });
              return (
                <Card key={a.id}>
                  <CardContent className="py-4 flex justify-between items-center">
                    <div>
                      <p className="font-medium">{emp ? `${emp.firstName} ${emp.lastName}` : 'Staff'}</p>
                      <p className="text-xs text-muted-foreground">{a.assignmentName}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Progress value={(a.progress || 0) * 100} className="w-24" />
                      <Badge variant={a.status === 'Completed' ? 'default' : 'outline'}>{a.status}</Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>
        )}
      </Tabs>

      {/* Journey Editor Dialog */}
      <Dialog open={journeyDialog} onOpenChange={setJourneyDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editJourney?.id ? 'Edit' : 'New'} Journey Template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Journey name" value={editJourney?.journeyName || ''} onChange={e => setEditJourney({ ...editJourney, journeyName: e.target.value })} />
            <Textarea placeholder="Description" value={editJourney?.description || ''} onChange={e => setEditJourney({ ...editJourney, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Select value={editJourney?.targetRole || ''} onValueChange={v => setEditJourney({ ...editJourney, targetRole: v })}>
                <SelectTrigger><SelectValue placeholder="Target Role" /></SelectTrigger>
                <SelectContent>{['Trainee','Staff','Supervisor','Manager'].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={editJourney?.status || 'Draft'} onValueChange={v => setEditJourney({ ...editJourney, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{['Draft','Active','Archived'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Textarea placeholder='Checklist items (JSON array, e.g. ["Tour the building","Meet the team"])' value={editJourney?.checklistItems || '[]'} onChange={e => setEditJourney({ ...editJourney, checklistItems: e.target.value })} rows={3} />
            <Textarea placeholder='Manager checklist items (JSON array)' value={editJourney?.managerChecklistItems || '[]'} onChange={e => setEditJourney({ ...editJourney, managerChecklistItems: e.target.value })} rows={2} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setJourneyDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveJourney}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign Journey to Staff</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={assignForm.employeeId} onValueChange={v => setAssignForm({ ...assignForm, employeeId: v })}>
              <SelectTrigger><SelectValue placeholder="Select staff member" /></SelectTrigger>
              <SelectContent>{(data?.staffList || []).map((s: any) => <SelectItem key={s.id} value={s.id}>{s.firstName} {s.lastName}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={assignForm.journeyId} onValueChange={v => setAssignForm({ ...assignForm, journeyId: v })}>
              <SelectTrigger><SelectValue placeholder="Select journey" /></SelectTrigger>
              <SelectContent>{(data?.journeys || []).filter((j: any) => j.status === 'Active').map((j: any) => <SelectItem key={j.id} value={j.id}>{j.journeyName}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleAssign}>Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
